import {
  Action,
  Alert,
  Form,
  FormSubmitHandler,
  openConfirmDialog,
  showNotification,
  ValidationErrors,
} from 'platform/components';
import {Show, Space, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import {
  TelephonyLine,
  useConfigureTelephonyLineRoutingMutation,
  useGetTelephonyGroupsQuery,
  useResetTelephonyLineRoutingMutation,
} from '@dms/api/settings';
import {ApiError} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {buildArray, Nullish, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {
  getConfigureTelephonyLineRoutingRequestBody,
  getTelephonyLineRoutingFormValues,
  telephonyLineRoutingFormSchema,
  TelephonyLineRoutingFormValues,
} from '../utils/telephonyLineRoutingFormSchema';
import {TelephonyLineRoutingFallbackCard} from './TelephonyLineRoutingFallbackCard';
import {TelephonyLineRoutingOpenTargetCard} from './TelephonyLineRoutingOpenTargetCard';

const BOT_UNAVAILABLE_ERROR_CODE = 'DMS-TE-RT-0002';

// The BE refuses a bot fallback when the tenant language is one the voice bot does not speak; the
// error belongs on whichever fallback asked for the bot.
const getBotUnavailableErrors = (values: TelephonyLineRoutingFormValues): ValidationErrors => {
  const message = i18n.t('page.settings.telephony.routingBotUnavailableInLanguage');

  return buildArray<ValidationErrors[number]>()
    .when(values.closedKind === 'bot', {name: 'closedKind', message})
    .when(values.noAnswerKind === 'bot', {name: 'noAnswerKind', message});
};

interface TelephonyLineRoutingTabProps extends RequiredTestIdProps {
  line: TelephonyLine | Nullish;
  isReadOnly: boolean;
}

export function TelephonyLineRoutingTab(props: TelephonyLineRoutingTabProps) {
  const navigate = useNavigate();

  // The default rule is shown read-only until the user asks to customize it; the form then starts
  // from the default's values so the saved rule never differs from what they saw.
  const [isCustomizing, setIsCustomizing] = useState(false);

  const {data: groups, isLoading: areGroupsLoading} = useGetTelephonyGroupsQuery();

  const [configureRouting] = useConfigureTelephonyLineRoutingMutation();
  const [resetRouting] = useResetTelephonyLineRoutingMutation();

  const line = props.line;

  if (isNil(line)) {
    return null;
  }

  const isDefault = line.routing.isDefault;
  const isShowingDefault = isDefault && !isCustomizing;
  const isFormReadOnly = props.isReadOnly || isShowingDefault;

  const handleSubmit: FormSubmitHandler<TelephonyLineRoutingFormValues> = async (
    values,
    setErrors,
    _reset,
    blockerContext
  ) => {
    const body = getConfigureTelephonyLineRoutingRequestBody(values);

    if (isNil(body)) {
      return;
    }

    await configureRouting({lineId: line.id, body})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        setIsCustomizing(false);

        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }

        navigate(settingsRoutes.telephony);
      })
      .catch((error: ApiError) => {
        const isBotUnavailable = error.data?.errors?.some(
          (item) => item.code === BOT_UNAVAILABLE_ERROR_CODE
        );

        if (isBotUnavailable) {
          setErrors(getBotUnavailableErrors(values));
          blockerContext?.reportApiFailure?.();
          return;
        }

        handleApiError(error, {signalApiError: blockerContext?.reportApiFailure});
      });
  };

  const handleReset = () =>
    openConfirmDialog({
      text: i18n.t('page.settings.telephony.routingResetConfirm'),
      'data-testid': suffixTestId('resetConfirm', props),
      onConfirm: () =>
        resetRouting({lineId: line.id})
          .unwrap()
          .then(() => {
            showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
            setIsCustomizing(false);
          })
          .catch(handleApiError),
    });

  return (
    <SettingsSection>
      <Form<TelephonyLineRoutingFormValues>
        // The default rule has no stored values; remount when it is customized so the form starts
        // from the defaults instead of a stale read-only snapshot.
        key={`${line.id}-${line.routing.isDefault}-${isCustomizing}`}
        experimentalZodSchema={telephonyLineRoutingFormSchema}
        onSubmit={handleSubmit}
        defaultValues={getTelephonyLineRoutingFormValues(line.routing, line.greetingOutOfHours)}
        shouldWatchForUnsavedChanges={!isShowingDefault}
      >
        {(control, formApi, navigateWithoutBlocker) => (
          <VStack spacing={4}>
            <Show when={isShowingDefault}>
              <Alert
                variant="info"
                layout="detailed"
                title={i18n.t('page.settings.telephony.routingDefaultTitle')}
                message={i18n.t('page.settings.telephony.routingDefaultDescription')}
                actions={buildArray<{
                  title: string;
                  onClick: () => void;
                  variant: 'primary';
                }>().whenNot(props.isReadOnly, {
                  title: i18n.t('page.settings.telephony.routingCustomize'),
                  variant: 'primary',
                  onClick: () => setIsCustomizing(true),
                })}
                data-testid={suffixTestId('defaultAlert', props)}
              />
            </Show>
            <Show when={isDefault && isCustomizing}>
              <Alert
                variant="info"
                message={i18n.t('page.settings.telephony.routingCustomizingHint')}
                data-testid={suffixTestId('customizingAlert', props)}
              />
            </Show>
            <Show when={!isShowingDefault}>
              <TelephonyLineRoutingOpenTargetCard
                control={control}
                groups={groups?.groups ?? []}
                areGroupsLoading={areGroupsLoading}
                isReadOnly={isFormReadOnly}
                data-testid={suffixTestId('openTarget', props)}
              />
              <TelephonyLineRoutingFallbackCard
                control={control}
                formApi={formApi}
                name="closedMessage"
                title={i18n.t('page.settings.telephony.routingClosedCardTitle')}
                description={i18n.t('page.settings.telephony.routingClosedCardDescription')}
                greetingOutOfHours={line.greetingOutOfHours}
                isReadOnly={isFormReadOnly}
                data-testid={suffixTestId('closedFallback', props)}
              />
              <TelephonyLineRoutingFallbackCard
                control={control}
                formApi={formApi}
                name="noAnswerMessage"
                title={i18n.t('page.settings.telephony.routingNoAnswerCardTitle')}
                description={i18n.t('page.settings.telephony.routingNoAnswerCardDescription')}
                greetingOutOfHours={line.greetingOutOfHours}
                isReadOnly={isFormReadOnly}
                data-testid={suffixTestId('noAnswerFallback', props)}
              />
            </Show>
            <Show when={!props.isReadOnly && !isShowingDefault}>
              <Space vertical={18} />
              <SettingsFooter
                actions={buildArray<Action>()
                  .whenNot(isDefault, {
                    type: 'button',
                    title: i18n.t('page.settings.telephony.routingResetToDefault'),
                    variant: 'dangerOutlined',
                    onClick: handleReset,
                    'data-testid': suffixTestId('reset', props),
                  })
                  .add({
                    type: 'button',
                    title: i18n.t('general.actions.discardChanges'),
                    variant: 'secondary',
                    onClick: () => navigateWithoutBlocker(settingsRoutes.telephony),
                    'data-testid': suffixTestId('discard', props),
                  })
                  .add({
                    type: 'form-button',
                    control,
                    title: i18n.t('general.actions.saveChanges'),
                    buttonType: 'submit',
                    variant: 'primary',
                    'data-testid': suffixTestId('save', props),
                  })}
              />
            </Show>
          </VStack>
        )}
      </Form>
    </SettingsSection>
  );
}
